import React, { useEffect, useState } from "react";
import { api } from "../../api/client";
import { Card } from "../../components/common/Card";
import { Badge } from "../../components/common/Badge";
import { DataTable, Column } from "../../components/common/Table";
import { Modal } from "../../components/common/Modal";

interface UserRow {
  id: number;
  employee_code: string | null;
  full_name: string;
  email: string;
  role_code: string;
  role_name: string;
  dept_name: string | null;
  is_active: number;
}

interface UserDetail {
  id: number;
  employee_code: string | null;
  full_name: string;
  email: string;
  phone: string | null;
  role_id: number;
  department_id: number | null;
  is_active: number;
}

interface Role {
  id: number;
  role_code: string;
  role_name: string;
  description: string;
}

interface Department {
  id: number;
  dept_code: string;
  dept_name: string;
}

interface Plant {
  id: number;
  plant_code: string;
  plant_name: string;
  description: string | null;
  is_active: number;
}

interface Unit {
  id: number;
  plant_id: number;
  plant_name?: string;
  unit_code: string;
  unit_name: string;
  description: string | null;
  is_active: number;
}

export default function Administration() {
  const [tab, setTab] = useState<"users" | "roles" | "plants">("users");
  const [users, setUsers] = useState<UserRow[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [plants, setPlants] = useState<Plant[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editUserId, setEditUserId] = useState<number | null>(null);
  const [showAddPlant, setShowAddPlant] = useState(false);
  const [showAddUnit, setShowAddUnit] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [u, r, d, p, un] = await Promise.all([
        api.get<UserRow[]>("/api/users"),
        api.get<Role[]>("/api/users/roles"),
        api.get<Department[]>("/api/master-data/departments"),
        api.get<Plant[]>("/api/master-data/plants"),
        api.get<Unit[]>("/api/master-data/units"),
      ]);
      setUsers(u);
      setRoles(r);
      setDepartments(d);
      setPlants(p);
      setUnits(un);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load administration data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const userColumns: Column<UserRow>[] = [
    { header: "Employee Code", accessor: (u) => u.employee_code ?? "-" },
    { header: "Name", accessor: (u) => u.full_name },
    { header: "Email", accessor: (u) => u.email },
    { header: "Role", accessor: (u) => u.role_name },
    { header: "Department", accessor: (u) => u.dept_name ?? "-" },
    { header: "Status", accessor: (u) => (u.is_active ? <Badge value="RUNNING" label="Active" /> : <Badge value="RETIRED" label="Inactive" />) },
  ];

  const roleColumns: Column<Role>[] = [
    { header: "Code", accessor: (r) => <span className="font-mono text-xs">{r.role_code}</span> },
    { header: "Name", accessor: (r) => r.role_name },
    { header: "Description", accessor: (r) => r.description },
  ];

  const plantColumns: Column<Plant>[] = [
    { header: "Code", accessor: (p) => <span className="font-mono text-xs">{p.plant_code}</span> },
    { header: "Name", accessor: (p) => p.plant_name },
    { header: "Status", accessor: (p) => (p.is_active ? <Badge value="RUNNING" label="Active" /> : <Badge value="RETIRED" label="Inactive" />) },
  ];

  const unitColumns: Column<Unit>[] = [
    { header: "Code", accessor: (u) => <span className="font-mono text-xs">{u.unit_code}</span> },
    { header: "Name", accessor: (u) => u.unit_name },
    { header: "Plant", accessor: (u) => u.plant_name ?? plants.find((p) => p.id === u.plant_id)?.plant_name ?? "-" },
    { header: "Status", accessor: (u) => (u.is_active ? <Badge value="RUNNING" label="Active" /> : <Badge value="RETIRED" label="Inactive" />) },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Administration</h1>
          <p className="text-sm text-slate-500">User management, RBAC roles, and master data setup.</p>
        </div>
        {tab === "users" && (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New User
          </button>
        )}
        {tab === "plants" && (
          <div className="flex gap-2">
            <button className="btn-secondary" onClick={() => setShowAddPlant(true)}>
              + New Plant
            </button>
            <button className="btn-primary" onClick={() => setShowAddUnit(true)}>
              + New Unit
            </button>
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <button onClick={() => setTab("users")} className={`rounded-full px-4 py-1.5 text-sm font-medium ${tab === "users" ? "bg-accent-500 text-white" : "bg-slate-100 dark:bg-industrial-700"}`}>
          Users
        </button>
        <button onClick={() => setTab("roles")} className={`rounded-full px-4 py-1.5 text-sm font-medium ${tab === "roles" ? "bg-accent-500 text-white" : "bg-slate-100 dark:bg-industrial-700"}`}>
          Roles
        </button>
        <button onClick={() => setTab("plants")} className={`rounded-full px-4 py-1.5 text-sm font-medium ${tab === "plants" ? "bg-accent-500 text-white" : "bg-slate-100 dark:bg-industrial-700"}`}>
          Plants & Units
        </button>
      </div>

      {tab === "plants" ? (
        <div className="grid gap-4 md:grid-cols-2">
          <Card>
            {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
            <h2 className="mb-3 text-sm font-semibold text-slate-500">Plants</h2>
            {loading ? (
              <p className="text-sm text-slate-400">Loading…</p>
            ) : (
              <DataTable
                columns={plantColumns}
                rows={plants}
                keyField={(p) => p.id}
              />
            )}
          </Card>
          <Card>
            <h2 className="mb-3 text-sm font-semibold text-slate-500">Units</h2>
            {loading ? (
              <p className="text-sm text-slate-400">Loading…</p>
            ) : (
              <DataTable columns={unitColumns} rows={units} keyField={(u) => u.id} />
            )}
          </Card>
        </div>
      ) : (
        <Card>
          {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
          {loading ? (
            <p className="text-sm text-slate-400">Loading…</p>
          ) : tab === "users" ? (
            <>
              <p className="mb-3 text-xs text-slate-400">Click a row to edit that user's details.</p>
              <DataTable columns={userColumns} rows={users} keyField={(u) => u.id} onRowClick={(u) => setEditUserId(u.id)} />
            </>
          ) : (
            <DataTable columns={roleColumns} rows={roles} keyField={(r) => r.id} />
          )}
        </Card>
      )}

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="New User">
        <NewUserForm
          roles={roles}
          onCreated={() => {
            setShowCreate(false);
            load();
          }}
        />
      </Modal>

      <Modal open={editUserId !== null} onClose={() => setEditUserId(null)} title="Edit User" wide>
        {editUserId !== null && (
          <EditUserForm
            userId={editUserId}
            roles={roles}
            departments={departments}
            onSaved={() => {
              setEditUserId(null);
              load();
            }}
          />
        )}
      </Modal>

      <Modal open={showAddPlant} onClose={() => setShowAddPlant(false)} title="New Plant">
        <NewPlantForm
          onCreated={() => {
            setShowAddPlant(false);
            load();
          }}
        />
      </Modal>

      <Modal open={showAddUnit} onClose={() => setShowAddUnit(false)} title="New Unit">
        <NewUnitForm
          plants={plants}
          onCreated={() => {
            setShowAddUnit(false);
            load();
          }}
        />
      </Modal>
    </div>
  );
}

function NewUserForm({ roles, onCreated }: { roles: Role[]; onCreated: () => void }) {
  const [form, setForm] = useState({ fullName: "", email: "", password: "", roleId: roles[0]?.id?.toString() ?? "" });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/api/auth/register", { ...form, roleId: Number(form.roleId) });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create user");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      {error && <p className="text-sm text-red-600">{error}</p>}
      <input className="input-field" placeholder="Full name" required value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
      <input className="input-field" type="email" placeholder="Email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
      <input className="input-field" type="password" placeholder="Temporary password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
      <select className="input-field" value={form.roleId} onChange={(e) => setForm({ ...form, roleId: e.target.value })}>
        {roles.map((r) => (
          <option key={r.id} value={r.id}>
            {r.role_name}
          </option>
        ))}
      </select>
      <button type="submit" disabled={submitting} className="btn-primary w-full">
        {submitting ? "Creating…" : "Create User"}
      </button>
    </form>
  );
}

function EditUserForm({
  userId,
  roles,
  departments,
  onSaved,
}: {
  userId: number;
  roles: Role[];
  departments: Department[];
  onSaved: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    employeeCode: "",
    phone: "",
    roleId: "",
    departmentId: "",
    isActive: true,
  });

  const [resetPassword, setResetPassword] = useState("");
  const [resettingPassword, setResettingPassword] = useState(false);
  const [resetMessage, setResetMessage] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const detail = await api.get<UserDetail>(`/api/users/${userId}`);
        setForm({
          fullName: detail.full_name ?? "",
          email: detail.email ?? "",
          employeeCode: detail.employee_code ?? "",
          phone: detail.phone ?? "",
          roleId: detail.role_id?.toString() ?? "",
          departmentId: detail.department_id?.toString() ?? "",
          isActive: !!detail.is_active,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load user");
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.put(`/api/users/${userId}`, {
        fullName: form.fullName,
        email: form.email,
        employeeCode: form.employeeCode || undefined,
        phone: form.phone || undefined,
        roleId: form.roleId ? Number(form.roleId) : undefined,
        departmentId: form.departmentId ? Number(form.departmentId) : undefined,
        isActive: form.isActive,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save user");
    } finally {
      setSubmitting(false);
    }
  };

  const doResetPassword = async () => {
    if (resetPassword.length < 8) {
      setResetMessage("New password must be at least 8 characters");
      return;
    }
    setResettingPassword(true);
    setResetMessage(null);
    try {
      await api.post(`/api/auth/reset-password/${userId}`, { newPassword: resetPassword });
      setResetMessage("Password reset successfully.");
      setResetPassword("");
    } catch (err) {
      setResetMessage(err instanceof Error ? err.message : "Failed to reset password");
    } finally {
      setResettingPassword(false);
    }
  };

  if (loading) return <p className="text-sm text-slate-400">Loading user…</p>;

  return (
    <div className="space-y-6">
      <form onSubmit={submit} className="space-y-3">
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Full name</label>
            <input className="input-field" required value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Email</label>
            <input className="input-field" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Employee code</label>
            <input className="input-field" value={form.employeeCode} onChange={(e) => setForm({ ...form, employeeCode: e.target.value })} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Phone</label>
            <input className="input-field" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Role</label>
            <select className="input-field" value={form.roleId} onChange={(e) => setForm({ ...form, roleId: e.target.value })}>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.role_name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Department</label>
            <select className="input-field" value={form.departmentId} onChange={(e) => setForm({ ...form, departmentId: e.target.value })}>
              <option value="">-</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.dept_name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
          Active (unchecking disables this account's login)
        </label>
        <button type="submit" disabled={submitting} className="btn-primary w-full">
          {submitting ? "Saving…" : "Save Changes"}
        </button>
      </form>

      <div className="border-t border-slate-200 dark:border-industrial-700 pt-4">
        <h3 className="mb-2 text-sm font-semibold text-slate-500">Reset Password (admin action)</h3>
        {resetMessage && <p className="mb-2 text-sm text-slate-600 dark:text-slate-300">{resetMessage}</p>}
        <div className="flex gap-2">
          <input
            type="password"
            className="input-field"
            placeholder="New password (min 8 characters)"
            value={resetPassword}
            onChange={(e) => setResetPassword(e.target.value)}
          />
          <button type="button" disabled={resettingPassword} className="btn-secondary whitespace-nowrap" onClick={doResetPassword}>
            {resettingPassword ? "Resetting…" : "Reset"}
          </button>
        </div>
      </div>
    </div>
  );
}

function NewPlantForm({ onCreated }: { onCreated: () => void }) {
  const [form, setForm] = useState({ plantCode: "", plantName: "", description: "" });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/api/master-data/plants", {
        plantCode: form.plantCode.toUpperCase(),
        plantName: form.plantName,
        description: form.description || undefined,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create plant");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      {error && <p className="text-sm text-red-600">{error}</p>}
      <input className="input-field" placeholder="Plant Code (e.g. SYN)" required value={form.plantCode} onChange={(e) => setForm({ ...form, plantCode: e.target.value })} />
      <input className="input-field" placeholder="Plant Name (e.g. Synthesis)" required value={form.plantName} onChange={(e) => setForm({ ...form, plantName: e.target.value })} />
      <input className="input-field" placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
      <button type="submit" disabled={submitting} className="btn-primary w-full">
        {submitting ? "Creating…" : "Create Plant"}
      </button>
    </form>
  );
}

function NewUnitForm({ plants, onCreated }: { plants: Plant[]; onCreated: () => void }) {
  const [form, setForm] = useState({ plantId: "", unitCode: "", unitName: "", description: "" });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.plantId) {
      setError("Plant is required");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.post("/api/master-data/units", {
        plantId: Number(form.plantId),
        unitCode: form.unitCode.toUpperCase(),
        unitName: form.unitName,
        description: form.description || undefined,
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create unit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      {error && <p className="text-sm text-red-600">{error}</p>}
      <select className="input-field" required value={form.plantId} onChange={(e) => setForm({ ...form, plantId: e.target.value })}>
        <option value="">Select Plant</option>
        {plants.map((p) => (
          <option key={p.id} value={p.id}>
            {p.plant_name}
          </option>
        ))}
      </select>
      <input className="input-field" placeholder="Unit Code (e.g. R25)" required value={form.unitCode} onChange={(e) => setForm({ ...form, unitCode: e.target.value })} />
      <input className="input-field" placeholder="Unit Name (e.g. Vessel R25)" required value={form.unitName} onChange={(e) => setForm({ ...form, unitName: e.target.value })} />
      <input className="input-field" placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
      <button type="submit" disabled={submitting} className="btn-primary w-full">
        {submitting ? "Creating…" : "Create Unit"}
      </button>
    </form>
  );
}
